import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import type { Cliente } from "../../types";

type PerfilForm = {
  persona_contacto: string;
  telefono: string;
  email_contacto: string;
  razon_social: string;
  cif: string;
  direccion: string;
  poblacion: string;
  codigo_postal: string;
  pais: string;
};

function formFromCliente(c: Cliente): PerfilForm {
  return {
    persona_contacto: c.persona_contacto,
    telefono: c.telefono,
    email_contacto: c.email_contacto,
    razon_social: c.razon_social,
    cif: c.cif,
    direccion: c.direccion,
    poblacion: c.poblacion,
    codigo_postal: c.codigo_postal,
    pais: c.pais,
  };
}

export function CuentaPage() {
  const { user, cliente, refreshUser } = useAuth();
  const [passwordForm, setPasswordForm] = useState({ password_actual: "", password_nueva: "", password_confirmar: "" });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "ok" | "error"; text: string } | null>(null);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<PerfilForm>({
    persona_contacto: "",
    telefono: "",
    email_contacto: "",
    razon_social: "",
    cif: "",
    direccion: "",
    poblacion: "",
    codigo_postal: "",
    pais: "España",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const resetFormFromCliente = useCallback(() => {
    if (cliente) setForm(formFromCliente(cliente));
  }, [cliente]);

  useEffect(() => {
    resetFormFromCliente();
  }, [resetFormFromCliente]);

  useEffect(() => {
    if (!success) return;
    const t = window.setTimeout(() => setSuccess(false), 3000);
    return () => window.clearTimeout(t);
  }, [success]);

  const handleEdit = () => {
    resetFormFromCliente();
    setError("");
    setEditing(true);
  };

  const handleCancel = () => {
    resetFormFromCliente();
    setError("");
    setEditing(false);
  };

  const handleSave = async () => {
    setError("");
    setSaving(true);
    try {
      await api.patch("/cliente/perfil", form);
      setEditing(false);
      await refreshUser();
      setSuccess(true);
    } catch (err: unknown) {
      const errorMsg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Error al guardar los datos";
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleCambiarPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    if (passwordForm.password_nueva.length < 8) {
      setMessage({ type: "error", text: "La nueva contraseña debe tener al menos 8 caracteres" });
      return;
    }
    if (passwordForm.password_nueva !== passwordForm.password_confirmar) {
      setMessage({ type: "error", text: "La confirmación no coincide" });
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/cambiar-password", {
        password_actual: passwordForm.password_actual,
        password_nueva: passwordForm.password_nueva,
      });
      setMessage({ type: "ok", text: "Contraseña actualizada correctamente" });
      setPasswordForm({ password_actual: "", password_nueva: "", password_confirmar: "" });
    } catch (err: unknown) {
      const errorMsg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Error al cambiar la contraseña";
      setMessage({ type: "error", text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-8">
      <h1 className="text-2xl font-semibold text-[#2C2C2C]">Mi cuenta</h1>

      <section className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-[#2C2C2C] mb-4">Datos de acceso</h2>
        <p className="text-sm text-gray-600">Email: <strong>{user?.email ?? "-"}</strong></p>
      </section>

      {cliente && (
        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-[#2C2C2C]">Datos de empresa</h2>
            {!editing && (
              <Button type="button" variant="secondary" onClick={handleEdit}>
                Editar
              </Button>
            )}
          </div>

          {success && (
            <p className="mb-4 text-sm text-green-600">Datos actualizados correctamente</p>
          )}
          {error && (
            <p className="mb-4 text-sm text-red-600">{error}</p>
          )}

          {!editing ? (
            <dl className="grid gap-2 text-sm">
              <dt className="font-medium text-gray-500">Razón social</dt><dd>{cliente.razon_social}</dd>
              <dt className="font-medium text-gray-500">CIF</dt><dd>{cliente.cif}</dd>
              <dt className="font-medium text-gray-500">Dirección</dt><dd>{cliente.direccion}, {cliente.codigo_postal} {cliente.poblacion}, {cliente.pais}</dd>
              <dt className="font-medium text-gray-500">Persona de contacto</dt><dd>{cliente.persona_contacto}</dd>
              <dt className="font-medium text-gray-500">Teléfono</dt><dd>{cliente.telefono}</dd>
              <dt className="font-medium text-gray-500">Email contacto</dt><dd>{cliente.email_contacto}</dd>
            </dl>
          ) : (
            <div className="space-y-4">
              <Input label="Razón social" value={form.razon_social} onChange={(e) => setForm((f) => ({ ...f, razon_social: e.target.value }))} required />
              <Input label="CIF" value={form.cif} onChange={(e) => setForm((f) => ({ ...f, cif: e.target.value }))} required />
              <Input label="Dirección" value={form.direccion} onChange={(e) => setForm((f) => ({ ...f, direccion: e.target.value }))} required />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input label="Código postal" value={form.codigo_postal} onChange={(e) => setForm((f) => ({ ...f, codigo_postal: e.target.value }))} required />
                <Input label="Población" value={form.poblacion} onChange={(e) => setForm((f) => ({ ...f, poblacion: e.target.value }))} required />
              </div>
              <Input label="País" value={form.pais} onChange={(e) => setForm((f) => ({ ...f, pais: e.target.value }))} required />
              <Input label="Persona de contacto" value={form.persona_contacto} onChange={(e) => setForm((f) => ({ ...f, persona_contacto: e.target.value }))} required />
              <Input label="Teléfono" value={form.telefono} onChange={(e) => setForm((f) => ({ ...f, telefono: e.target.value }))} required />
              <Input label="Email contacto" type="email" value={form.email_contacto} onChange={(e) => setForm((f) => ({ ...f, email_contacto: e.target.value }))} required />
              <div className="flex gap-3 pt-2">
                <Button type="button" loading={saving} onClick={handleSave}>
                  Guardar
                </Button>
                <Button type="button" variant="secondary" disabled={saving} onClick={handleCancel}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </section>
      )}

      <section className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-[#2C2C2C] mb-4">Cambiar contraseña</h2>
        <form onSubmit={handleCambiarPassword} className="space-y-4">
          <Input type="password" label="Contraseña actual" value={passwordForm.password_actual} onChange={(e) => setPasswordForm((f) => ({ ...f, password_actual: e.target.value }))} required autoComplete="current-password" />
          <Input type="password" label="Nueva contraseña (mín. 8 caracteres)" value={passwordForm.password_nueva} onChange={(e) => setPasswordForm((f) => ({ ...f, password_nueva: e.target.value }))} required minLength={8} autoComplete="new-password" />
          <Input type="password" label="Confirmar nueva contraseña" value={passwordForm.password_confirmar} onChange={(e) => setPasswordForm((f) => ({ ...f, password_confirmar: e.target.value }))} required autoComplete="new-password" />
          {message && <p className={message.type === "ok" ? "text-green-600 text-sm" : "text-red-600 text-sm"}>{message.text}</p>}
          <Button type="submit" loading={loading}>Cambiar contraseña</Button>
        </form>
      </section>
    </div>
  );
}
